<template>
  <div>
    <!-- App Bar Principal -->
    <v-app-bar
      color="primary"
      app
      elevation="4"
      :height="64"
    >
      <div class="container d-flex align-center px-2">
        <!-- Logo -->
        <router-link to="/" class="text-decoration-none text-white font-weight-medium text-h6 mr-10">
          Les<span class="font-weight-bold">Actualites</span>.fr
        </router-link>
        
        <!-- Navigation principale -->
        <v-toolbar-items class="hidden-sm-and-down flex-grow-1">
          <v-btn
            v-for="item in displayNavItems"
            :key="item.title"
            :to="item.to"
            text
            class="text-white text-subtitle-2 font-weight-medium mx-1"
            :class="{ 'v-btn--active': isActive(item.to) }"
          >
            {{ item.title }}
          </v-btn>
          
          <!-- Bouton Catégories -->
          <v-menu
            offset-y
            max-height="400"
            :close-on-content-click="false"
            transition="slide-y-transition"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                text
                class="text-white text-subtitle-2 font-weight-medium mx-1"
              >
                <v-icon small class="mr-1">mdi-plus</v-icon>
                Catégories
              </v-btn>
            </template>
            <v-card min-width="300">
              <v-card-title class="text-subtitle-1 font-weight-medium py-3">
                Toutes les catégories
              </v-card-title>
              <v-divider></v-divider>
              <v-list density="compact" class="pa-0">
                <v-list-item
                  v-for="category in allCategories"
                  :key="category._id || category.slug"
                  :to="`/category/${category.slug}`"
                  class="py-1"
                >
                  <template v-slot:prepend>
                    <v-avatar size="32" :color="getCategoryColor(category._id || category.slug)" class="mr-3 white--text">
                      <v-icon size="16" color="white">{{ category.icon || 'mdi-tag' }}</v-icon>
                    </v-avatar>
                  </template>
                  <v-list-item-title>{{ category.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </v-toolbar-items>
        
        <!-- Actions -->
        <div class="d-flex align-center">
          <v-btn icon class="mr-2">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
          
          <v-btn
            v-if="isAuthenticated"
            icon
            class="mr-2"
          >
            <v-icon>mdi-bell</v-icon>
            <v-badge 
              v-if="hasNotifications"
              color="error" 
              content="3" 
              overlap
            ></v-badge>
          </v-btn>
          
          <template v-if="isAuthenticated">
            <v-menu
              offset-y
              transition="slide-y-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-avatar size="35">
                    <v-img
                      v-if="user && user.avatar"
                      :src="user.avatar"
                      alt="Avatar"
                    ></v-img>
                    <v-icon v-else>mdi-account-circle</v-icon>
                  </v-avatar>
                </v-btn>
              </template>
              
              <v-list dense>
                <v-list-item :to="{ name: 'Profile' }">
                  <v-list-item-icon>
                    <v-icon>mdi-account-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Mon Profil</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item :to="{ name: 'MySources' }">
                  <v-list-item-icon>
                    <v-icon>mdi-rss</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Mes Sources</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                
                <v-divider></v-divider>
                
                <v-list-item @click="logout">
                  <v-list-item-icon>
                    <v-icon>mdi-logout</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Déconnexion</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          
          <template v-else>
            <v-btn
              text
              :to="{ name: 'Login' }"
              class="text-white text-subtitle-2 font-weight-medium"
            >Connexion</v-btn>
            
            <v-btn
              color="white"
              class="primary--text"
              :to="{ name: 'Register' }"
              small
              depressed
            >S'inscrire</v-btn>
          </template>
        </div>
      </div>
    </v-app-bar>
    
    <!-- Tabs Navigation -->
    <v-app-bar
      color="white"
      app
      flat
      :height="48"
      class="tabs-nav"
      :style="{ top: '64px' }"
    >
      <div class="container px-2">
        <div class="d-flex align-center justify-space-between">
          <v-tabs
            :model-value="activeTab"
            @update:model-value="setActiveTab"
            color="primary"
            :height="48"
            slider-size="3"
            show-arrows
          >
            <v-tab
              v-for="item in tabNavItems"
              :key="item.value"
              :value="item.value"
              class="text-body-2 font-weight-medium"
              min-width="100"
            >
              <v-icon small class="mr-2">{{ item.icon }}</v-icon>
              {{ item.title }}
            </v-tab>
          </v-tabs>
          
        </div>
      </div>
    </v-app-bar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'MainHeader',
  
  data() {
    return {
      staticNavItems: [
        { title: 'Découvrir', to: '/' },
        { title: 'Actualités', to: '/category/actualites' },
        { title: 'Sports', to: '/category/sport' },
        { title: 'Économie', to: '/category/economie' },
        { title: 'Tech', to: '/category/technologie' },
        { title: 'Culture', to: '/category/culture' }
        // "International" a été remplacé par le bouton Catégories
      ],
      
      tabNavItems: [
        { title: 'ACTUALITÉS', value: 'standard', icon: 'mdi-clock-outline' },
        { title: 'TENDANCE', value: 'trending', icon: 'mdi-chart-line' },
        { title: 'POPULAIRE', value: 'popular', icon: 'mdi-fire' },
        { title: 'MES SOURCES', value: 'mySources', icon: 'mdi-rss' }
      ],
      
      hasNotifications: true
    }
  },
  
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/isAuthenticated',
      user: 'auth/user',
      activeTab: 'ui/activeTab',
      navCategories: 'ui/navCategories',
      allCategories: 'ui/allCategories',
      getCategoryColor: 'ui/getCategoryColor'
    }),
    
    // Items combinés pour la navigation principale
    displayNavItems() {
      if (this.navCategories && this.navCategories.length > 0) {
        // Transformer les catégories dynamiques au format de la navigation
        const dynamicItems = this.navCategories.map(cat => ({
          title: cat.name,
          to: `/category/${cat.slug}`
        }));
        
        // Retourner la combinaison des items statiques et dynamiques
        return this.staticNavItems;
      }
      
      // Par défaut, retourner les éléments statiques
      return this.staticNavItems;
    }
  },
  
  watch: {
    activeTab(newValue) {
      // Mise à jour de l'interface en fonction du store
      // (pas besoin d'émettre, on utilise le store)
    }
  },
  
  mounted() {
    // Charger les catégories au montage du composant
    this.loadCategories();
  },
  
  methods: {
    ...mapActions({
      logoutAction: 'auth/logout',
      fetchCategories: 'ui/fetchCategories'
    }),
    
    isActive(route) {
      return this.$route.path === route;
    },
    
    async logout() {
      try {
        await this.logoutAction();
        this.$router.push('/');
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
      }
    },
    
    setActiveTab(tab) {
      this.$store.dispatch('ui/setActiveTab', tab);
    },
    
    async loadCategories() {
      try {
        await this.fetchCategories();
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      }
    }
  }
}
</script>

<style scoped>
.container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
}

.v-btn--active {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.15);
}

.tabs-nav {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  z-index: 90;
}

.article-count {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}
</style>